import { useMemo } from 'react';
import { useQuery } from 'react-query';

import {
  useNamespace,
  useOkapiKy,
} from '@folio/stripes/core';

import { OKAPI_TENANT_HEADER } from '../../common/constants';

const INITIAL_DATA = [];

const useUserTenantPermissions = (
  { userId, tenantId },
  options = {},
) => {
  const ky = useOkapiKy();
  const api = ky.extend({
    hooks: {
      beforeRequest: [(req) => req.headers.set(OKAPI_TENANT_HEADER, tenantId)],
    },
  });
  const [namespace] = useNamespace({ key: 'user-affiliation-permissions' });

  const searchParams = {
    full: 'true',
    indexField: 'userId',
  };

  const {
    isFetching,
    isLoading,
    data = {},
  } = useQuery(
    [namespace, userId, tenantId],
    ({ signal }) => {
      return api.get(
        `perms/users/${userId}/permissions`,
        {
          searchParams,
          signal,
        },
      ).json();
    },
    {
      enabled: Boolean(userId && tenantId),
      keepPreviousData: true,
      ...options,
    },
  );

  const tenantPermissions = data.permissionNames || INITIAL_DATA;

  const flattenTenantPermissions = useMemo(() => {
    const permSet = new Set();

    tenantPermissions.forEach(perm => {
      permSet.add(perm.permissionName);

      perm.subPermissions?.forEach(subPermission => {
        permSet.add(subPermission);
      });
    });

    return permSet;
  }, [tenantPermissions]);

  return ({
    isFetching,
    isLoading,
    userPermissions: flattenTenantPermissions,
    totalRecords: data.totalRecords,
  });
};

export default useUserTenantPermissions;
